import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import Avatar from "@mui/material/Avatar"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import IconButton from "@mui/material/IconButton"
import { useEffect, useRef, useState } from "react"
import ReactCrop from "react-image-crop"

import DialogHeader from "../components/DialogHeader"

export default function AvatarEditor({
  popoverAnchorElemRef,
  croppedImageDataURL,
  onEditButtonClicked,
  onNewImageSelected,
  inputElemRef,
  imageDataURL,
  handleCropPopoverClosed,
  handleCropFinished,
  cropPopoverOpen,
  children,
}) {
  return (
    <Box
      ref={popoverAnchorElemRef}
      sx={{
        display: "block",
        position: "relative",
        alignSelf: { xs: "center", sm: "flex-start" },
      }}
    >
      {croppedImageDataURL ? (
        <Avatar
          sx={{ width: "120px", height: "120px" }}
          src={croppedImageDataURL}
        />
      ) : (
        children
      )}
      <IconButton
        size="small"
        color="primary"
        onClick={onEditButtonClicked}
        sx={(theme) => ({
          position: "absolute",
          bottom: 0,
          right: 0,
          backgroundColor: theme.palette.colors.brand100,
          "&:hover": { backgroundColor: theme.palette.colors.gray200 },
        })}
      >
        <EditOutlinedIcon />
      </IconButton>
      <input
        type="file"
        style={{ display: "none" }}
        accept="image/*"
        onChange={onNewImageSelected}
        ref={inputElemRef}
      />
      <CropImageDialog
        imgData={imageDataURL}
        onClosed={handleCropPopoverClosed}
        onCropFinished={handleCropFinished}
        open={cropPopoverOpen}
      />
    </Box>
  )
}

export function useAvatarEditorState() {
  const [imageDataURL, setImageDataURL] = useState("")
  const [imageName, setImageName] = useState("")
  const [croppedImageDataURL, setCroppedImageDataURL] = useState("")
  const [croppedImageBlob, setCroppedImageBlob] = useState("")
  const inputElemRef = useRef()
  const popoverAnchorElemRef = useRef()
  const [cropPopoverOpen, setCropPopoverOpen] = useState(false)

  function handleEditButtonClicked() {
    inputElemRef.current.click()
  }

  function handleNewImageSelected(e) {
    if (e.target.files && e.target.files.length > 0) {
      const fileReader = new FileReader()
      fileReader.addEventListener("load", () => {
        if (fileReader.result) {
          setImageName(e.target.files[0].name)
          setImageDataURL(fileReader.result.toString())
          setCropPopoverOpen(true)
        }
      })
      fileReader.readAsDataURL(e.target.files[0])
    }
  }

  function handleCropFinished(cropDataURL, cropBlob) {
    setCroppedImageDataURL(cropDataURL)
    setCroppedImageBlob(cropBlob)
  }

  function handleCropPopoverClosed() {
    setCropPopoverOpen(false)
    setImageDataURL("")
    if (inputElemRef.current) {
      inputElemRef.current.value = ""
    }
  }

  return {
    popoverAnchorElemRef,
    inputElemRef,
    handleEditButtonClicked,
    handleNewImageSelected,
    handleCropFinished,
    handleCropPopoverClosed,
    cropPopoverOpen,
    croppedImageBlob,
    imageName,
    croppedImageDataURL,
    setCroppedImageDataURL,
    imageDataURL,
  }
}

const CROP_MODAL_MAX_WIDTH_PX = 400

function CropImageDialog({ open, onClosed, imgData, onCropFinished }) {
  const [imageCrop, setImageCrop] = useState({
    unit: "px",
    x: 0,
    y: 0,
    width: 100,
    height: 100,
  })
  const imageElemRef = useRef()
  const [displayWidth, setDisplayWidth] = useState(0)
  const canvasRef = useRef()
  const ASPECT_RATIO = 1

  function applyCrop(crop) {
    if (!crop?.width || !crop?.height) {
      return
    }

    const canvas2dContext = canvasRef.current.getContext("2d")
    const image = imageElemRef.current
    if (!canvas2dContext || !image) {
      return
    }

    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height
    const pixelRatio = window.devicePixelRatio
    canvasRef.current.width = Math.floor(crop.width * scaleX * pixelRatio)
    canvasRef.current.height = Math.floor(crop.height * scaleY * pixelRatio)
    canvas2dContext.imageSmoothingQuality = "high"
    canvas2dContext.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
    canvas2dContext.save()
    canvas2dContext.drawImage(
      imageElemRef.current,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    )
    canvas2dContext.restore()
    canvasRef.current.toBlob((blob) => {
      if (!blob) return
      onCropFinished(canvasRef.current.toDataURL(), blob)
    })
  }

  function handleUploadClick() {
    applyCrop(imageCrop)
    onClosed()
  }

  function handleCancelClick() {
    onClosed()
  }

  useEffect(() => {
    if (!imgData) {
      setDisplayWidth(0)
      return
    }

    const image = new Image()
    image.src = imgData
    image.onload = () => {
      const scale = Math.min(1, CROP_MODAL_MAX_WIDTH_PX / image.naturalWidth)
      const displayW = Math.round(image.naturalWidth * scale)
      const displayH = Math.round(image.naturalHeight * scale)
      const initialCropSize = Math.min(displayW, displayH)

      setDisplayWidth(displayW)
      setImageCrop({
        unit: "px",
        x: 0,
        y: 0,
        width: initialCropSize,
        height: initialCropSize,
      })
    }
  }, [imgData])

  return (
    <Dialog
      open={open}
      onClose={onClosed}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          border: 1,
          borderColor: "divider",
        },
      }}
    >
      <DialogHeader title="Upload profile photo" onClose={onClosed} />
      <DialogContent
        sx={{
          px: 2,
          py: 2,
          display: "flex",
          justifyContent: "center",
          borderTop: 1,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? "background.default"
              : theme.palette.grey[50],
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: displayWidth > 0 ? displayWidth : CROP_MODAL_MAX_WIDTH_PX,
          }}
        >
          <ReactCrop
            crop={imageCrop}
            onChange={(crop) => setImageCrop(crop)}
            aspect={ASPECT_RATIO}
            circularCrop
          >
            <img
              alt="crop"
              ref={imageElemRef}
              src={imgData}
              style={{
                display: "block",
                maxWidth: "100%",
                width: displayWidth > 0 ? displayWidth : "100%",
                height: "auto",
              }}
            />
          </ReactCrop>
        </Box>
        <canvas
          ref={canvasRef}
          style={{
            display: "none",
          }}
        />
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Button variant="outlined" onClick={handleCancelClick}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUploadClick}
          disabled={!imageCrop?.width || !imageCrop?.height}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  )
}
